import { format, parse } from 'date-fns';
import { take } from 'lodash';
import moment from 'moment';

import { MenuItem } from '@/components/V2/DasboardSidebar/components/SidebarItem';
import { CartItem } from '@/types/Cart';
import { DeliveryFrequencyType, EzshipAutoOrderDetail } from '@/types/EzShip';
import { ProductStrapi } from '@/types/Product';

import { daysList } from '../constants/data';
import { roundToTwoDecimalPlaces } from './numberHelpers';

export const generalDateFormat = 'MM/DD/YYYY';
export const generalDateTimeFormat = `${generalDateFormat} hh:mm:ss`;
export const generalEnrollmentDateFormat = 'MM\xa0/\xa0DD\xa0/\xa0YYYY';

export const getNextTwentyYears = () => {
  return Array.from({ length: 21 }, (_, i) => new Date().getFullYear() + i);
};

export const OpenUrlInNewTab = (url?: string) => {
  if (url) return window.open(url, '_blank');
};

export const capitalizeFirstLetter = (string: string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export function replaceLastPartOfUrl(url: string, newPart: string): string {
  try {
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split('/');

    pathSegments[pathSegments.length - 1] = newPart;
    parsedUrl.pathname = pathSegments.join('/');

    return parsedUrl.toString();
  } catch (ex) {
    return '';
  }
}

export const getValidUrl = (url: string) => {
  const isAbsoluteUrl = /^https?:\/\//i.test(url);
  return isAbsoluteUrl ? url : `http://${url}`;
};

export const getAbbreviation = (text: string) => {
  const firstTwoItems = take((text || '').split(' '), 2);
  const result = firstTwoItems.reduce((acc, curr) => acc + curr[0] || '', '');

  return result === 'undefined' ? '' : result;
};

export const getNextProcessDate = (currentDate: string, frequencyType: number = 3, specificDayInterval: number = 7) => {
  const date = moment(currentDate).isValid() ? new Date(currentDate) : new Date();
  let days = 7;
  if (frequencyType === 12 && specificDayInterval) {
    let findDaysCount = daysList.find(item => item.value === specificDayInterval.toString())?.value;
    days = findDaysCount ? parseInt(findDaysCount) : 7;
  }

  const returnValue = moment(date).format(generalDateFormat);

  switch (frequencyType.toString()) {
    case '12': //Days
      date.setDate(date.getDate() + days);
      break;
    case '1': //Weekly
      date.setDate(date.getDate() + 7);
      break;
    case '2': //Biweekly
      date.setDate(date.getDate() + 14);
      break;
    case '3': //Monthly
      date.setMonth(date.getMonth() + 1);
      break;
    case '4': //Quarterly
      date.setMonth(date.getMonth() + 3);
      break;
    case '7': //Bimonthly
      date.setMonth(date.getMonth() + 2);
      break;
    case '8': //Every 4 Weeks
      date.setDate(date.getDate() + 28);
      break;
    case '9': //Every 6 Weeks
      date.setDate(date.getDate() + 42);
      break;
    case '10': //Every 8 Weeks
      date.setDate(date.getDate() + 56);
      break;
    case '11': //Every 12 Weeks
      date.setDate(date.getDate() + 84);
      break;
    default:
      return returnValue;
  }
  return returnValue;
};

export const checkIfAnyAutoOrderProdcutIsOutOfStock = (data?: EzshipAutoOrderDetail): boolean => {
  if (data) {
    return data.itemDetails.items.some(item => item.isOutOfStock === true);
  }
  return false;
};

export const checkIfAnyAutoOrderProductIsNotSkipped = (data?: EzshipAutoOrderDetail) => {
  const excludedCodes = new Set(['P-WEBFEE', 'P-PETPROACCESS', 'coupon']);

  return data?.itemDetails?.items
    ?.filter(item => {
      const itemCode = item.itemCode.trim().toUpperCase();
      const itemDescription = item.itemDescription.toLowerCase();

      return !excludedCodes.has(itemCode) && !itemDescription.includes('fee') && !itemDescription.includes('discount');
    })
    .some(item => !item.isSkipped);
};

export const getDaysCountForDeliveryPreference = (days?: string): string => {
  if (days) {
    try {
      let dayCount = days.split(' day')[0];
      return dayCount ?? '7';
    } catch (error) {
      return '7';
    }
  }
  return '7';
};

export const getPreviousThirtyYears = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const years = [];
  for (let i = 0; i < 31; i++) {
    years.push(currentYear - i);
  }

  return years;
};

export const formateDateWithoutTime = (
  value?: string | null,
  showTime?: boolean,
  format: string = generalDateFormat
) => {
  if (!value) return value;
  const date = new Date(value);
  const dateFormat = showTime ? generalDateTimeFormat : format;
  const formatedDate = moment(date).format(dateFormat);

  return formatedDate === 'Invalid date' || formatedDate === '1/1/0001' || formatedDate === '01/01/0001'
    ? 'N/A'
    : formatedDate;
};

export const tableNAValueHandler = (value: string | number | null | undefined) => {
  if (typeof value === 'string' && value.trim() === '') {
    return 'N/A';
  }
  if (value === 0 || (value !== null && value !== undefined)) {
    return value;
  }
  return 'N/A';
};

export const rankTextColor = [
  {
    rankID: 0,
    color: '#78A2E2',
    rankDescription: 'New petPro',
  },
  {
    rankID: 10,
    color: '#9D9090',
    rankDescription: 'petPro',
  },
  {
    rankID: 20,
    color: '#3B2122',
    rankDescription: 'Senior petPro',
  },
  {
    rankID: 30,
    color: '#8390A4',
    rankDescription: 'Director',
  },
  {
    rankID: 40,
    color: '#082149',
    rankDescription: 'Senior Director',
  },
  {
    rankID: 50,
    color: '#EFC6B6',
    rankDescription: 'Executive Director',
  },
  {
    rankID: 60,
    color: '#E08D6E',
    rankDescription: 'Vice President',
  },
  {
    rankID: 70,
    color: '#91A29A',
    rankDescription: 'One Star VP',
  },
  {
    rankID: 80,
    color: '#5A7468',
    rankDescription: 'Two Star VP',
  },
  {
    rankID: 90,
    color: '#234635',
    rankDescription: 'Three Star VP',
  },
];

export const getFullMonthName = (shortName: string) => {
  const map = {
    Jan: 'January',
    Feb: 'February',
    Mar: 'March',
    Apr: 'April',
    May: 'May',
    Jun: 'June',
    Jul: 'July',
    Aug: 'August',
    Sep: 'September',
    Oct: 'October',
    Nov: 'November',
    Dec: 'December',
  };
  const key = (shortName.charAt(0).toUpperCase() + shortName.slice(1, 3).toLowerCase()) as keyof typeof map;
  return map[key] || null;
};

export const getEndOfMonthDate = (shortName: any, year = new Date().getFullYear()) => {
  const monthMap = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  const m: string = shortName.charAt(0).toUpperCase() + shortName.slice(1, 3).toLowerCase();
  const monthIndex = monthMap[m as keyof typeof monthMap];
  if (monthIndex === undefined) return null;
  return new Date(year, monthIndex + 1, 0).getDate();
};

export const extractRunIdFromDescription = (description: string) => {
  const match = description.match(/BiMonthly\s+(\d+)/);
  return match ? match[1] : 'N/A';
};

export const checkRouteMatch = (
  item: MenuItem,
  pathname: string,
  isRoot: boolean = true
): {
  isExactMatch: boolean;
  isChildActive: boolean;
  highlightThisLevel: boolean;
} => {
  const isEZShipManager = item.name === 'EZ Ship Manager' && pathname === '/AutoOrders/auto-order-list';
  const isEZShipsChild = item.name === 'EZ Ships' && pathname === '/AutoOrders/auto-order-list';
  const isFlaggedForManager = localStorage.getItem('active-ez-ship-manager') === 'true';

  const blockThisMatch = (isEZShipManager && !isFlaggedForManager) || (isEZShipsChild && isFlaggedForManager);

  const isExactMatch = !!item.link && pathname === item.link && !blockThisMatch;

  if (isExactMatch) {
    return {
      isExactMatch: true,
      isChildActive: false,
      highlightThisLevel: false,
    };
  }

  if (item.subMenu?.length) {
    for (const child of item.subMenu) {
      const childMatch = checkRouteMatch(child, pathname, false);

      if (childMatch.isExactMatch || childMatch.isChildActive) {
        return {
          isExactMatch: false,
          isChildActive: true,
          highlightThisLevel: isRoot,
        };
      }
    }
  }

  return {
    isExactMatch: false,
    isChildActive: false,
    highlightThisLevel: false,
  };
};

export const removeDecimal = (value: string | number): string => {
  const decimalValue = roundToTwoDecimalPlaces(value);
  const cleaned = typeof decimalValue === 'string' ? decimalValue.replace(/,/g, '') : decimalValue;
  const num = typeof cleaned === 'string' ? parseFloat(cleaned) : cleaned;

  if (isNaN(num)) return '0';

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.trunc(num));
};

export function checkIfAllProductsInCart(a: ProductStrapi[], cart: CartItem[]): boolean {
  if (a.length === 0 || cart.length === 0) {
    return false;
  }

  // Only consider products that are in stock
  const inStockProducts = a.filter(product => !product.attributes.OutOfStock);

  if (inStockProducts.length === 0) {
    return false; // If no products are in stock, can't have all products in cart
  }

  return inStockProducts.every(product => {
    const id = product.attributes.ItemCode;
    return cart.some(c => String(c.id) === String(id));
  });
}
const defaultFreeShippingMessage = 'Add 3 Qualifying Items For FREE SHIPPING!';
const oneFreeShippingMessage = 'Add 2 Qualifying Items For FREE SHIPPING!';
const twoFreeShippingMessage = 'Add 1 Qualifying Item For FREE SHIPPING!';
const freeShippingMessage = 'FREE SHIPPING!';

export const getFreeShippingMessage = (carts?: CartItem[]) => {
  if (!carts) {
    return defaultFreeShippingMessage;
  }

  let cartFreeShippingProductCount = carts.reduce((count, cart) => {
    if (cart.isFreeShipping && cart.isEZShip) {
      return count + (cart.quantity || 0);
    }
    return count;
  }, 0);

  if (cartFreeShippingProductCount > 3) {
    cartFreeShippingProductCount = 3;
  }

  switch (cartFreeShippingProductCount) {
    case 1:
      return oneFreeShippingMessage;
    case 2:
      return twoFreeShippingMessage;
    case 3:
      return freeShippingMessage;
    default:
      return defaultFreeShippingMessage;
  }
};

export const getFreeShippingMessageByCount = (count?: number) => {
  if (!count) {
    return defaultFreeShippingMessage;
  }
  if (count > 3) {
    count = 3;
  }

  switch (count) {
    case 1:
      return oneFreeShippingMessage;
    case 2:
      return twoFreeShippingMessage;
    case 3:
      return freeShippingMessage;
    default:
      return defaultFreeShippingMessage;
  }
};

export const normalizeAddress = (address: string): string => {
  if (!address) return address;
  return (
    address
      .trim()
      .toLowerCase()
      // Remove all spaces around special characters and numbers
      .replace(/\s*([-#.,0-9])\s*/g, '$1')
      // Remove spaces between other characters as well
      .replace(/\s+/g, '')
  );
};

export const isDuplicateAddress = (addresses: any[], newAddress: any, editingAddress?: any): boolean => {
  if (addresses.length === 0) return false;
  return addresses?.some(addr => {
    if (editingAddress && addr.addressType === editingAddress.addressType) {
      return false;
    }

    return (
      normalizeAddress(addr.address1) === normalizeAddress(newAddress.address1) &&
      normalizeAddress(addr.address2) === normalizeAddress(newAddress.address2)
    );
  });
};

export const getStartDateFromResponse = (data?: DeliveryFrequencyType): Date => {
  if (!data) return new Date();

  const { lastRunDate, createdDate } = data;

  if (lastRunDate && lastRunDate !== '-') {
    const parsedLastRunDate = new Date(lastRunDate);
    if (!isNaN(parsedLastRunDate.getTime())) return parsedLastRunDate;
  }

  if (createdDate) {
    const parsedCreatedDate = new Date(createdDate);
    if (!isNaN(parsedCreatedDate.getTime())) return parsedCreatedDate;
  }

  return new Date();
};

export const avatarFileToBase64 = (file: any): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = error => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};

export const getCurrentYear = (date?: Date | string | number | null): string => {
  try {
    const d = date ? new Date(date) : new Date();
    if (isNaN(d.getTime())) return '2026';
    return d.getFullYear().toString();
  } catch (error) {
    return '2026';
  }
};

export type DateRangeSplitPart = 'startDate' | 'endDate';

/** Splits a combined range (e.g. DD-MM-YYYY_DD-MM-YYYY) on "_" and returns that segment as yyyy-MM-dd. */
export const getDateRangeSplitPart = (value: string, part: DateRangeSplitPart): string => {
  const DateFormat = 'yyyy-MM-dd';
  const segmentFormat = 'dd-MM-yyyy';

  if (!value?.trim()) return '';

  const [start, end] = value.split('_');
  const segment = part === 'startDate' ? start ?? '' : end ?? '';
  if (!segment) return '';

  const parsed = parse(segment, segmentFormat, new Date());
  if (Number.isNaN(parsed.getTime())) return '';

  return format(parsed, DateFormat);
};

export const getCurrentAndNextThreeMonthsOptions = (): Array<{ value: string; name: string }> => {
  const options: Array<{ value: string; name: string }> = [];

  for (let i = 0; i < 4; i++) {
    const startDate = moment().add(i, 'month').startOf('month');
    const endDate = moment(startDate).endOf('month');

    options.push({
      value: `${startDate.format('DD-MM-YYYY')}_${endDate.format('DD-MM-YYYY')}`,
      name: `${startDate.format('MMMM YYYY')} (${startDate.format(generalDateFormat)} - ${endDate.format(
        generalDateFormat
      )})`,
    });
  }

  return options;
};

export const parseStringObjectValueFromStrapi = (input: string): string =>
  (() => {
    try {
      const o = JSON.parse(input);
      return o?.visible ? o.value ?? '' : '';
    } catch {
      return input;
    }
  })();
